import type { SkillData } from '../spacetime/modules/SkillModule';
import type { AppearanceCatalogEntry, ItemCatalogEntry, MobCatalogEntry } from '../spacetime/types';
import { windowId, windowRegistry } from './windowing';
import type { WindowHandle } from './windowing/WindowRegistry';
import { CodexPanel } from './CodexPanel';
import { buildCodexTabDefinitions, type CodexTab } from './codexShared';
import { dispatchCodexChatInsert } from './codexChatLinks';
import { buildSkillBarSignature, buildSkillListSignature } from './skillListStability';

export class CodexWindow {
  private readonly container: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly contentArea: HTMLElement;
  private readonly onOpenCallbacks: Array<() => void> = [];
  private readonly onCloseCallbacks: Array<() => void> = [];
  private playerSkills: SkillData[] = [];
  private items: ItemCatalogEntry[] = [];
  private mobs: MobCatalogEntry[] = [];
  private appearances: AppearanceCatalogEntry[] = [];
  private skillBarSkillIds: string[] = [];
  private playerSkillsSignature = '';
  private skillBarSkillIdsSignature = '';
  private defaultPosition = { x: 0, y: 0 };
  private readonly defaultSize = { width: 420, height: 420 };
  private readonly MIN_WIDTH = 320;
  private readonly MIN_HEIGHT = 280;
  private readonly windowHandle: WindowHandle;
  private readonly panel: CodexPanel;

  constructor(container: HTMLElement, closeBtn: HTMLButtonElement) {
    this.container = container;
    const header = this.container.querySelector<HTMLElement>('.window-header');
    const resizeHandle = this.container.querySelector<HTMLElement>('.window-resize-handle');
    const contentArea = this.container.querySelector<HTMLElement>('.window-content');
    if (!header || !resizeHandle || !contentArea) {
      throw new Error('Codex window is missing drag/resize elements');
    }
    this.contentArea = contentArea;
    this.closeBtn = closeBtn;
    this.centerWindow();
    this.windowHandle = windowRegistry.register({
      id: windowId('codex', 'main'),
      container: this.container as HTMLDivElement,
      header: header as HTMLDivElement,
      resizeHandle: resizeHandle as HTMLDivElement,
      closeButton: this.closeBtn,
      minWidth: this.MIN_WIDTH,
      minHeight: this.MIN_HEIGHT,
      defaultPosition: this.defaultPosition,
      defaultSize: this.defaultSize,
      defaultTab: 'skills',
      allowedTabs: ['skills', 'items', 'mobs', 'appearances'],
    });
    this.panel = new CodexPanel({
      contentArea,
      initialTab: this.windowHandle.getActiveTab() as CodexTab,
      getTabs: () =>
        buildCodexTabDefinitions(this.createSnapshot()).map((tab) => ({
          ...tab,
          getRows: () => tab.getRows().map((row) => ({
            ...row,
            onDoubleClick: (value: string) => {
              dispatchCodexChatInsert({
                kind: tab.key,
                value,
                label: row.title,
              });
            },
          })),
        })),
      onActiveTabChange: (tab) => this.windowHandle.setActiveTab(tab),
    });
    header.after(this.panel.getTabBarElement());
    this.closeBtn.addEventListener('click', () => this.hide());
    if (this.windowHandle.getWindowState() === 'open') {
      this.show();
    } else {
      this.hide();
    }
  }

  public updateSkills(skills: SkillData[]): void {
    const nextSignature = buildSkillListSignature(skills);
    if (nextSignature === this.playerSkillsSignature) {
      return;
    }
    this.playerSkills = skills;
    this.playerSkillsSignature = nextSignature;
    this.render();
  }

  public updateSkillBar(skillIds: string[]): void {
    const nextSignature = buildSkillBarSignature(skillIds);
    if (nextSignature === this.skillBarSkillIdsSignature) {
      return;
    }
    this.skillBarSkillIds = skillIds;
    this.skillBarSkillIdsSignature = nextSignature;
    this.render();
  }

  public updateItems(items: ItemCatalogEntry[]): void {
    this.items = items;
    this.render();
  }

  public updateMobs(mobs: MobCatalogEntry[]): void {
    this.mobs = mobs;
    this.render();
  }

  public updateAppearances(appearances: AppearanceCatalogEntry[]): void {
    this.appearances = appearances;
    this.render();
  }

  public openTab(tab: CodexTab): void {
    if (!this.panel.hasTab(tab)) return;
    this.panel.setActiveTab(tab);
    this.show();
  }

  public isOpenOnTab(tab: CodexTab): boolean {
    return this.isVisible && this.panel.getActiveTab() === tab;
  }

  public show(): void {
    if (!this.container.hidden) return;
    this.container.hidden = false;
    this.windowHandle.setWindowState('open');
    this.onOpenCallbacks.forEach((callback) => callback());
    this.render();
  }

  public hide(): void {
    if (this.container.hidden) return;
    this.container.hidden = true;
    this.windowHandle.setWindowState('closed');
    this.onCloseCallbacks.forEach((callback) => callback());
  }

  public get isVisible(): boolean {
    return !this.container.hidden;
  }

  public onOpen(callback: () => void): void {
    this.onOpenCallbacks.push(callback);
  }

  public onClose(callback: () => void): void {
    this.onCloseCallbacks.push(callback);
  }

  private render(): void {
    if (!this.isVisible) return;
    this.contentArea.style.overflowY = 'hidden';
    this.panel.render();
  }

  private createSnapshot() {
    return {
      playerSkills: this.playerSkills,
      items: this.items,
      mobs: this.mobs,
      appearances: this.appearances,
      skillBarSkillIds: this.skillBarSkillIds,
    };
  }

  private centerWindow(): void {
    this.defaultPosition = {
      x: Math.max(0, Math.round((window.innerWidth - this.defaultSize.width) / 2)),
      y: Math.max(0, Math.round((window.innerHeight - this.defaultSize.height) / 2)),
    };
  }

  public destroy(): void {
    this.panel.destroy();
    this.windowHandle.destroy();
  }
}
